import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/MemberstackAuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ContactDialog from '../components/ContactDialog';

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [loginComplete, setLoginComplete] = useState(false);
  const { login, hasActivePlan, isAuthenticated, member, checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasNavigated = useRef(false);

  // Effect to handle navigation after login when auth state fully updates
  useEffect(() => {
    if (loginComplete && isAuthenticated && !hasNavigated.current) {
      // Give a small delay for state to fully settle
      const timer = setTimeout(() => {
        if (hasNavigated.current) return;
        
        const hasPlan = hasActivePlan();
        console.log('Post-login check - hasActivePlan:', hasPlan, 'member:', member);
        
        hasNavigated.current = true;
        
        if (hasPlan) {
          toast.success('Welcome back!');
          navigate('/app', { replace: true });
        } else {
          toast.success('Welcome! Please select a subscription plan.');
          navigate('/subscribe', { replace: true });
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [loginComplete, isAuthenticated, hasActivePlan, member, navigate]);

  const handleLogin = async () => {
    setLoading(true);
    hasNavigated.current = false;
    
    try {
      const result = await login();
      console.log('Login complete, raw result:', JSON.stringify(result, null, 2));
      
      // Log the structure to understand where planConnections are
      if (result?.data) {
        console.log('Login result.data keys:', Object.keys(result.data));
        console.log('planConnections at result.data.planConnections:', result.data.planConnections);
        console.log('member after login:', result.data);
      }
      
      // Mark login as complete - the useEffect will handle navigation
      // after the auth context state has updated
      setLoginComplete(true);
      
      // Also refresh auth to ensure we have latest data
      await checkAuth();
      
    } catch (err) {
      if (err.message !== 'Modal closed') {
        toast.error('Login failed. Please try again.');
      }
      setLoginComplete(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Left side - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md space-y-8 animate-in">
            <div>
              <h1 className="text-4xl font-heading font-bold tracking-tight">Bõkr</h1>
              <p className="text-muted-foreground mt-2">Sign in to your account</p>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleLogin}
                className="w-full"
                size="lg"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/signup" className="text-primary hover:underline" data-testid="signup-link">
                  Create one
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Branding */}
        <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-12">
          <div className="max-w-md text-primary-foreground">
            <h2 className="text-3xl font-heading font-bold mb-4">
              Schedule. Invoice. Get Paid.
            </h2>
            <p className="text-primary-foreground/80">
              Bõkr helps service businesses manage their time, track client balances, 
              and get paid faster. All in one beautiful interface.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 px-8 border-t border-border bg-background">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs text-muted-foreground">
          <span>© 2026 GetBokr. Operated by Lifely Holdings Limited (NZBN 9429053450883).</span>
          <a href="/privacy-policy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms-of-service" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <button onClick={() => setContactOpen(true)} className="hover:text-foreground transition-colors">
            Contact
          </button>
        </div>
      </footer>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
